import { useSetAtom, useAtom } from 'jotai';
import {
  userBetsRecordState,
  rushBetMapAtom,
  moonRushInfo,
  rushBetAmountListAtom,
  userWinRateAtom,
  currentUserPointAtom,
} from '../../state';
import { useEffect } from 'react';
import {
  RushGameData,
  RushGameInfo,
  RushGameStatus,
  RushWsServerData,
} from '@/components/features/casino/rush/types';
import useGlobalWebSocket from '@/hooks/useGlobalWebsocket';
import { WebSocketCategoryEnum, WebSocketCommandsEnum } from '@/types/WS';
import { getRushGameInfo } from '../../api';
import { RUSH_DELAY } from '@/components/features/casino/rush/Wheel/config';
import { useCelebration } from '@/components/features/casino/game-celebration';
import { formatMoneyByCurrency } from '@/utils';
import { PRIZEMaps } from '@/components/features/casino/rush/constances';
import { useUser } from '@/state/userInfo/hooks';

export const moonRushGameId = 'rush';

const useUpdateRushBets = () => {
  const setRushBets = useSetAtom(userBetsRecordState);
  const updateRushBets = (data) => {
    setRushBets((prev) => {
      return [
        ...prev,
        {
          ...data.data,
        },
      ];
    });
  };
  useEffect(() => {
    return () => {
      setRushBets([]);
    };
  }, []);
  useGlobalWebSocket({
    category: WebSocketCategoryEnum.RUSH_GAME,
    command: WebSocketCommandsEnum.RUSH_BET_EVENT,
    onMessage: ({ data }) => {
      setTimeout(() => {
        updateRushBets(data);
      }, RUSH_DELAY);
    },
  });
};

const useUpdateRushGameInfo = () => {
  const setUserPoint = useSetAtom(currentUserPointAtom);
  const setRushInfo = useSetAtom(moonRushInfo);
  const { celebrate } = useCelebration();
  const setRushBetAmount = useSetAtom(rushBetAmountListAtom);
  const updateRushInfo = (rushInfo: RushGameInfo) => {
    const {
      gameId,
      roundId,
      status,
      currentGamePoint = '0',
      grandPrize,
      totalGamePoint,
      nextRoundIn = 10000,
      betValues,
      rushPoint,
      restAward,
      winner = null,
      trigger = null,
      currentUserPoint,
    } = rushInfo;
    if (betValues && Array.isArray(betValues)) {
      setRushBetAmount(betValues);
    }
    setRushInfo((info) => {
      info.gameId = gameId;
      info.gameRoundId = roundId;
      info.gameStatus = status;
      info.poolAmount = restAward;
      info.currentGamePoint = currentGamePoint;
      if (currentUserPoint) {
        info.currentUserPoint = currentUserPoint;
      }
      info.totalGamePoint = totalGamePoint;
      info.grandPrizeAmount = grandPrize;
      info.countdown = Number(nextRoundIn) / 1000;
      info.rushPoint = rushPoint;
      info.winner = winner;
      info.trigger = trigger;
      return info;
    });
  };

  const { isCurrentUser, name } = useUser();
  const handleGameEnd = ({ winner, grandPrize }: RushGameInfo) => {
    const winnerName = () => {
      if (winner?.hiddenIdentity) {
        if (isCurrentUser(winner?.uid)) {
          return name;
        } else {
          return 'Hidden';
        }
      } else {
        return winner?.name;
      }
    };
    celebrate({
      profit: formatMoneyByCurrency(grandPrize) + ' ' + 'USDT',
      rewardValue: 'GRAND PRIZE',
      winner: winnerName(),
    });
  };

  // init rush game state
  useEffect(() => {
    getRushGameInfo().then((data) => {
      if (!data) return;
      updateRushInfo(data);
      setUserPoint(Number(data.currentUserPoint) || 0);
    });
  }, []);

  const { sendJsonMessage, readyState } = useGlobalWebSocket({
    category: WebSocketCategoryEnum.RUSH_GAME,
    command: WebSocketCommandsEnum.RUSH_STATUS_NOTIFY_COMMAND,
    onMessage: ({ data }) => {
      try {
        updateRushInfo(data.data as RushGameInfo);
        // console.log('🐔🐔🐔ws push', data.data);
        if (data?.data?.status === RushGameStatus.END) {
          console.log('🐔🐔🐔game end', data.data);
          handleGameEnd(data.data as RushGameInfo);
        }
        if (data?.data?.status === RushGameStatus.PREPARING) {
          setUserPoint(0);
        }
      } catch (e) {
        console.log(e, data);
      }
    },
  });

  useEffect(() => {
    sendJsonMessage({
      gameId: moonRushGameId,
      category: WebSocketCategoryEnum.RUSH_GAME,
      command: WebSocketCommandsEnum.GAME_ENTER_REQUEST,
    });

    return () => {
      sendJsonMessage({
        gameId: moonRushGameId,
        category: WebSocketCategoryEnum.RUSH_GAME,
        command: WebSocketCommandsEnum.GAME_QUIT_REQUEST,
      });
    };
  }, [readyState]);
};

export default function UseRushWebSocketSubscribe() {
  useUpdateRushBets();
  useUpdateRushGameInfo();
  return <></>;
}
